/**
 * Overview (D-39): how notifications are doing, with one filter row that scopes every number below it.
 */
import type { Channel, GeoOverview, Overview } from "@aiyo/shared";
import { ArrowDownRight, ArrowUpRight, ChartColumn, ChevronRight, MapPin } from "@gravity-ui/icons";
import { Card, Skeleton } from "@heroui/react";
import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { DailyBars, Heatmap, Meter } from "../components/charts";
import { CheckboxInput } from "../components/fields";
import { FilterSelect, Segmented } from "../components/filters";
import { IssueChip } from "../components/issues";
import { ChannelChip, EmptyBlock, ErrorBlock, LoadingBlock, Page, PageHeader, Panel } from "../components/ui";
import { browserTimeZone } from "../lib/hooks";
import { useApps, useGeo, useOverview } from "../lib/queries";

// MapLibre is large, so the map loads only when the overview shows it.
const GeoMap = lazy(() => import("../components/GeoMap"));

const PERIODS = ["1", "7", "30", "90"] as const;
type Period = (typeof PERIODS)[number];

function useNumbers() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "id" ? "id-ID" : "en-US";
  return useMemo(
    () => ({
      int: new Intl.NumberFormat(locale),
      compact: new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }),
      pct: new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 1 }),
    }),
    [locale],
  );
}

export function OverviewPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const apps = useApps();
  const period: Period = (PERIODS as readonly string[]).includes(params.get("days") ?? "")
    ? (params.get("days") as Period)
    : "7";
  const appId = params.get("app") ?? undefined;
  const includeTest = params.get("test") === "1";
  const tz = useMemo(browserTimeZone, []);
  const overview = useOverview({
    app_id: appId,
    days: Number(period),
    tz,
    include_test: includeTest || undefined,
  });

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  return (
    <Page>
      <PageHeader title={t("overview.title")} description={t("overview.subtitle")} />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <FilterSelect
          label={t("overview.app")}
          allLabel={t("overview.allApps")}
          value={appId}
          onChange={(v) => update({ app: v })}
          options={(apps.data ?? []).map((a) => ({ id: a.id, label: a.name }))}
          className="w-full sm:w-56"
        />
        <Segmented
          label={t("overview.period")}
          value={period}
          onChange={(v) => update({ days: v === "7" ? undefined : v })}
          options={PERIODS.map((p) => ({ id: p, label: t(`overview.periods.${p}`) }))}
        />
        <div className="sm:ml-auto">
          <CheckboxInput
            variant="primary"
            label={t("overview.includeTest")}
            isSelected={includeTest}
            onChange={(v) => update({ test: v ? "1" : undefined })}
          />
        </div>
      </div>

      {overview.isPending ? (
        <LoadingBlock />
      ) : overview.isError ? (
        <ErrorBlock error={overview.error} onRetry={() => overview.refetch()} />
      ) : (
        <div
          className={`flex flex-col gap-6 transition-opacity ${overview.isPlaceholderData ? "opacity-60" : ""}`}
          aria-busy={overview.isFetching}
        >
          <Dashboard data={overview.data} period={period} appId={appId} />
          <MapPanel appId={appId} days={Number(period)} includeTest={includeTest} />
        </div>
      )}
    </Page>
  );
}

function Dashboard({ data, period, appId }: { data: Overview; period: Period; appId?: string }) {
  const { t } = useTranslation();
  const n = useNumbers();
  const [metric, setMetric] = useState<"volume" | "problems">("volume");
  const { totals, previous } = data;
  const settled = totals.total - totals.in_progress;
  const reached = totals.delivered + totals.accepted;
  const prevSettled = previous.total - previous.in_progress;
  const rate = (part: number, whole: number) => (whole > 0 ? part / whole : null);
  const logLink = (extra: Record<string, string>) => {
    const q = new URLSearchParams({ days: period === "90" ? "30" : period, ...extra });
    if (appId) q.set("app", appId);
    return `/logs?${q.toString()}`;
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiTile
          label={t("overview.kpi.deliveries")}
          value={n.int.format(totals.total)}
          delta={change(totals.total, previous.total)}
          upIsGood
        />
        <KpiTile
          label={t("overview.kpi.reached")}
          value={rate(reached, settled) === null ? "—" : n.pct.format(rate(reached, settled)!)}
          hint={t("overview.kpiHint.reached")}
          delta={pointChange(
            rate(reached, settled),
            rate(previous.delivered + previous.accepted, prevSettled),
          )}
          upIsGood
        />
        <KpiTile
          label={t("overview.kpi.problems")}
          value={n.int.format(totals.problems)}
          hint={t("overview.kpiHint.problems")}
          delta={change(totals.problems, previous.problems)}
          upIsGood={false}
          href={totals.problems ? logLink({ outcome: "problems" }) : undefined}
        />
        <KpiTile label={t("overview.kpi.messages")} value={n.int.format(data.messages)} />
        <KpiTile
          label={t("overview.kpi.customers")}
          value={n.int.format(data.customers.total)}
          hint={t("overview.kpiHint.customers", { count: data.customers.new })}
        />
      </div>

      {totals.total === 0 ? (
        <Card>
          <EmptyBlock icon={ChartColumn} title={t("overview.empty")} hint={t("overview.emptyHint")} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
          <Panel
            className="lg:col-span-2"
            title={t("overview.heatmap.title")}
            description={t("overview.heatmap.description", { tz: data.range.tz })}
            actions={
              <Segmented
                label={t("overview.heatmap.metric")}
                value={metric}
                onChange={setMetric}
                options={[
                  { id: "volume", label: t("overview.heatmap.volume") },
                  { id: "problems", label: t("overview.heatmap.problems") },
                ]}
              />
            }
          >
            <Heatmap
              values={data.heatmap.total}
              problems={data.heatmap.problems}
              hue={metric === "volume" ? "accent" : "danger"}
              metric={metric}
            />
          </Panel>

          <Panel title={t("overview.issues.title")} description={t("overview.issues.description")}>
            <IssueList data={data} logLink={logLink} />
          </Panel>

          <Panel className="lg:col-span-2" title={t("overview.daily.title")}>
            <DailyBars days={data.daily} />
          </Panel>

          <Panel title={t("overview.channels.title")}>
            <ChannelList data={data} />
          </Panel>
        </div>
      )}
    </>
  );
}

function change(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return (current - previous) / previous;
}

/** Difference between two rates, in percentage points. */
function pointChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null;
  return current - previous;
}

function KpiTile({
  label,
  value,
  hint,
  delta,
  upIsGood,
  href,
}: {
  label: string;
  value: string;
  hint?: string;
  delta?: number | null;
  upIsGood?: boolean;
  href?: string;
}) {
  const { t } = useTranslation();
  const n = useNumbers();
  const showDelta = delta !== undefined;
  const flat = delta === null || delta === undefined || Math.abs(delta) < 0.0005;
  const good = !flat && delta! > 0 === upIsGood;
  const content = (
    <Card className="h-full gap-2 p-5">
      <div className="text-sm text-muted">{label}</div>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
      {showDelta ? (
        delta === null ? (
          <div className="text-xs text-muted">{t("overview.noPrevious")}</div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs">
            {flat ? null : delta! > 0 ? (
              <ArrowUpRight className={`size-3.5 ${good ? "text-success" : "text-danger"}`} aria-hidden />
            ) : (
              <ArrowDownRight className={`size-3.5 ${good ? "text-success" : "text-danger"}`} aria-hidden />
            )}
            <span className={flat ? "text-muted" : good ? "text-success" : "text-danger"}>
              {flat ? "±0" : `${delta! > 0 ? "+" : ""}${n.pct.format(delta!)}`}
            </span>
            <span className="text-muted">{t("overview.vsPrevious")}</span>
          </div>
        )
      ) : hint ? (
        <div className="text-xs text-muted">{hint}</div>
      ) : null}
    </Card>
  );
  if (!href) return content;
  return (
    <Link to={href} className="rounded-[28px] outline-none focus-visible:ring-2 focus-visible:ring-focus">
      {content}
    </Link>
  );
}

function IssueList({ data, logLink }: { data: Overview; logLink: (q: Record<string, string>) => string }) {
  const { t } = useTranslation();
  const n = useNumbers();
  if (data.issues.length === 0) {
    return <p className="text-sm text-muted">{t("overview.issues.none")}</p>;
  }
  const max = Math.max(...data.issues.map((i) => i.count));
  const top = data.issues.slice(0, 6);
  return (
    <div className="flex flex-col gap-3">
      <ul className="-mx-2 flex flex-col">
        {top.map((i) => (
          <li key={i.issue}>
            <Link
              to={logLink({ issue: i.issue })}
              className="group flex flex-col gap-2 rounded-xl px-2 py-2.5 outline-none hover:bg-default focus-visible:ring-2 focus-visible:ring-focus"
            >
              <div className="flex items-center justify-between gap-3">
                <IssueChip issue={i.issue} />
                <span className="flex items-center gap-1 text-sm font-medium tabular-nums">
                  {n.int.format(i.count)}
                  <ChevronRight className="size-3.5 text-muted transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
              <Meter value={i.count / max} label={t(`issues.${i.issue}.label`)} tone="danger" />
            </Link>
          </li>
        ))}
      </ul>
      <Link to={logLink({ outcome: "problems" })} className="self-start text-sm text-accent hover:underline">
        {t("overview.issues.openLog")}
      </Link>
    </div>
  );
}

function ChannelList({ data }: { data: Overview }) {
  const { t } = useTranslation();
  const n = useNumbers();
  return (
    <ul className="flex flex-col gap-4">
      {data.per_channel.map((c) => {
        const settled = c.total - c.in_progress;
        const reach = settled > 0 ? (c.delivered + c.accepted) / settled : 0;
        return (
          <li key={c.channel} className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <ChannelChip channel={c.channel as Channel} />
              <span className="text-sm font-medium">
                {t("overview.channels.rate", { rate: n.pct.format(reach) })}
              </span>
            </div>
            <Meter value={reach} label={t("overview.channels.rate", { rate: n.pct.format(reach) })} />
            <span className="text-xs text-muted">
              {t("overview.channels.deliveries", { count: n.int.format(c.total) })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function MapPanel({ appId, days, includeTest }: { appId?: string; days: number; includeTest: boolean }) {
  const { t } = useTranslation();
  const n = useNumbers();
  const [metric, setMetric] = useState<"customers" | "problems">("customers");
  const geo = useGeo({ app_id: appId, days, include_test: includeTest || undefined });
  const data = geo.data;
  const located = data ? data.totals.exact + data.totals.approximate : 0;

  return (
    <Panel
      title={t("overview.map.title")}
      description={t("overview.map.description")}
      actions={
        <Segmented
          label={t("overview.map.metric")}
          value={metric}
          onChange={setMetric}
          options={[
            { id: "customers", label: t("overview.map.metricCustomers") },
            { id: "problems", label: t("overview.map.metricProblems") },
          ]}
        />
      }
    >
      {geo.isPending ? (
        <Skeleton className="h-[320px] sm:h-[420px] w-full rounded-2xl" />
      ) : geo.isError ? (
        <ErrorBlock error={geo.error} onRetry={() => geo.refetch()} />
      ) : !data || located === 0 ? (
        <EmptyBlock icon={MapPin} title={t("overview.map.empty")} />
      ) : (
        <div
          className={`grid grid-cols-1 gap-6 transition-opacity lg:grid-cols-[minmax(0,1fr)_18rem] ${geo.isPlaceholderData ? "opacity-60" : ""}`}
        >
          <div className="flex min-w-0 flex-col gap-3">
            <Suspense fallback={<Skeleton className="h-[320px] sm:h-[420px] w-full rounded-2xl" />}>
              <GeoMap points={data.points} metric={metric} />
            </Suspense>
            <p className="text-xs text-muted">
              {t("overview.map.coverage", {
                exact: n.int.format(data.totals.exact),
                approximate: n.int.format(data.totals.approximate),
                unlocated: n.int.format(data.totals.unlocated),
              })}
            </p>
          </div>
          <AreaList areas={data.areas} appId={appId} metric={metric} />
        </div>
      )}
    </Panel>
  );
}

/** The table view of the map: top areas, each linking to its customers. */
function AreaList({
  areas,
  appId,
  metric,
}: {
  areas: GeoOverview["areas"];
  appId?: string;
  metric: "customers" | "problems";
}) {
  const { t } = useTranslation();
  const n = useNumbers();
  const sorted = [...areas].sort((a, b) => b[metric] - a[metric] || b.customers - a.customers);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between px-2 text-xs text-muted">
        <span>{t("overview.map.areas")}</span>
        <span className="flex gap-4">
          <span className="w-14 text-right">{t("overview.map.customers")}</span>
          <span className="w-14 text-right">{t("overview.map.metricProblems")}</span>
        </span>
      </div>
      <ul className="flex flex-col">
        {sorted.map((a) => {
          const name = a.city ?? a.region ?? "—";
          const q = new URLSearchParams({ city: name });
          if (appId) q.set("app", appId);
          return (
            <li key={`${name}-${a.country ?? ""}`}>
              <Link
                to={`/customers?${q.toString()}`}
                aria-label={t("overview.map.viewCustomers", { area: name })}
                className="flex items-center justify-between gap-3 rounded-xl px-2 py-2 text-sm outline-none hover:bg-default focus-visible:ring-2 focus-visible:ring-focus"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium">{name}</span>
                  {a.city && a.region ? (
                    <span className="truncate text-xs text-muted">{a.region}</span>
                  ) : null}
                </span>
                <span className="flex shrink-0 gap-4 tabular-nums">
                  <span className="w-14 text-right">{n.int.format(a.customers)}</span>
                  <span className={`w-14 text-right ${a.problems ? "text-danger" : "text-muted"}`}>
                    {n.int.format(a.problems)}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
